import React, { useMemo } from "react";

// Reusa tus componentes
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Button from "components/CustomButtons/CustomButton";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";

import PrintIcon from "@material-ui/icons/PictureAsPdf";
import TableRowsIcon from "@material-ui/icons/TableChart";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import Typography from "@material-ui/core/Typography";

// Colors derived from #13816F
const COLORS = {
  main: "#13816F",
  headLight: "#19A58D",
  pastel1: "#CFF0EA",
  pastel2: "#E3F7F3",
  faded: "#F3FBF9",
};

/** Helpers */
const fmtMoney = (v) =>
  typeof v === "number"
    ? v.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const fmtMoney0 = (v) =>
  typeof v === "number"
    ? v.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    : Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

const fmtDateTime = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
};

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
};

export default function ReportEndorsementsSimple({
  title = "",
  infos = [],
  forma = {},
  report_tz = "",
  timezones = {},
  onGeneratePdf,
  onGenerateCsv,
  onBack,
}) {
  // Global totals across all agencies
  const globalTotals = useMemo(() => {
    const accum = {
      quantity: 0,
      net: 0,
      policy_fee: 0,
      iva: 0,
      total: 0,
      agency_fee: 0,
      agent_fee: 0,
      commission: 0,
    };

    infos.forEach((info) => {
      const sumList = (list, sign = 1) => {
        list.forEach((r) => {
          accum.quantity += sign * 1;
          accum.net += sign * (Number(r.revised_net_premium) || 0);
          accum.policy_fee += sign * (Number(r.policy_fee) || 0);
          accum.iva += sign * (Number(r.iva) || 0);
          accum.total += sign * (Number(r.total) || 0);
          accum.agency_fee += sign * (Number(r.agency_fee) || 0);
          accum.agent_fee += sign * (Number(r.agent_fee) || 0);
          accum.commission += sign * (Number(r.commission) || 0);
        });
      };

      sumList(info.agent?.all || [], +1);
      sumList(info.guest?.all || [], +1);
      // cancellations are negative effect (refunds)
      sumList(info.agent?.cancel || [], -1);
      sumList(info.guest?.cancel || [], -1);
    });

    return accum;
  }, [infos]);

  /** Renders a simple endorsement table for a list (rows) */
  const EndorsementTable = ({ titleLabel, rows }) => {
    if (!rows || rows.length === 0) return null;

    // compute subtotal
    const subtotal = rows.reduce(
      (s, r) => {
        s.quantity += 1;
        s.net += Number(r.revised_net_premium) || 0;
        s.policy_fee += Number(r.policy_fee) || 0;
        s.iva += Number(r.iva) || 0;
        s.total += Number(r.total) || 0;
        s.agency_fee += Number(r.agency_fee) || 0;
        s.agent_fee += Number(r.agent_fee) || 0;
        s.commission += Number(r.commission) || 0;
        return s;
      },
      {
        quantity: 0,
        net: 0,
        policy_fee: 0,
        iva: 0,
        total: 0,
        agency_fee: 0,
        agent_fee: 0,
        commission: 0,
      }
    );

    return (
      <>
        <TableRow style={{ background: COLORS.pastel1 }}>
          <TableCell>Policy #</TableCell>
          <TableCell>Issue Date</TableCell>
          <TableCell>From Date</TableCell>
          <TableCell>Client Name</TableCell>
          <TableCell style={{ textAlign: "right" }}>Net Prem.</TableCell>
          <TableCell style={{ textAlign: "right" }}>Policy Fee</TableCell>
          <TableCell style={{ textAlign: "right" }}>Mex IVA</TableCell>
          <TableCell style={{ textAlign: "right" }}>Total Prem.</TableCell>
          <TableCell style={{ textAlign: "right" }}>Agent Fee</TableCell>
          <TableCell style={{ textAlign: "right" }}>Agency Fee</TableCell>
          {/* commission column */}
          <TableCell style={{ textAlign: "right" }}>Commission</TableCell>
          <TableCell>Reason</TableCell>
        </TableRow>

        {rows.map((r, idx) => (
          <TableRow key={idx}>
            <TableCell>{r.policy_nbr}</TableCell>
            <TableCell>{fmtDateTime(r.created)}</TableCell>
            <TableCell>{fmtDate(r.effective_date)}</TableCell>
            <TableCell style={{ textTransform: "uppercase" }}>
              {r.client_name}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(r.revised_net_premium)}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(r.policy_fee)}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(r.iva)}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(r.total)}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(r.agent_fee)}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(r.agency_fee)}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(r.commission)}
            </TableCell>
            <TableCell>{r.reason || ""}</TableCell>
          </TableRow>
        ))}

        {/* subtotal row */}
        <TableRow style={{ background: COLORS.pastel2, fontWeight: 700 }}>
          <TableCell colSpan={3}></TableCell>
          <TableCell>Total: {subtotal.quantity}</TableCell>
          <TableCell style={{ textAlign: "right" }}>
            ${fmtMoney(subtotal.net)}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            ${fmtMoney(subtotal.policy_fee)}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            ${fmtMoney(subtotal.iva)}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            ${fmtMoney(subtotal.total)}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            ${fmtMoney(subtotal.agent_fee)}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            ${fmtMoney(subtotal.agency_fee)}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            ${fmtMoney(subtotal.commission)}
          </TableCell>
          <TableCell></TableCell>
        </TableRow>
      </>
    );
  };

  /** Render cancellations grouped by tipo */
  const CancellationsByTipo = ({ rows }) => {
    if (!rows || rows.length === 0) return null;

    // group by tipo
    const groups = rows.reduce((acc, r) => {
      const key = r.tipo || "Unknown";
      if (!acc[key]) acc[key] = [];
      acc[key].push(r);
      return acc;
    }, {});

    return Object.entries(groups).map(([tipo, items]) => {
      // compute subtotal (note: in original PHP cancellations were treated as negative on totals)
      const subtotal = items.reduce(
        (s, r) => {
          s.quantity += 1;
          s.net += Number(r.revised_net_premium) || 0;
          s.policy_fee += Number(r.policy_fee) || 0;
          s.iva += Number(r.iva) || 0;
          s.total += Number(r.total) || 0;
          s.agency_fee += Number(r.agency_fee) || 0;
          s.agent_fee += Number(r.agent_fee) || 0;
          s.commission += Number(r.commission) || 0;
          return s;
        },
        {
          quantity: 0,
          net: 0,
          policy_fee: 0,
          iva: 0,
          total: 0,
          agency_fee: 0,
          agent_fee: 0,
          commission: 0,
        }
      );

      return (
        <React.Fragment key={tipo}>
          <TableRow style={{ background: COLORS.pastel1 }}>
            <TableCell colSpan={12}>{tipo}s</TableCell>
          </TableRow>

          <TableRow style={{ background: COLORS.pastel1 }}>
            <TableCell>Policy # test</TableCell>
            <TableCell>Issue Date</TableCell>
            <TableCell>From Date</TableCell>
            <TableCell>Client Name</TableCell>
            <TableCell style={{ textAlign: "right" }}>Net Refund</TableCell>
            <TableCell style={{ textAlign: "right" }}>Policy Fee</TableCell>
            <TableCell style={{ textAlign: "right" }}>Mex IVA</TableCell>
            <TableCell style={{ textAlign: "right" }}>Total Refund</TableCell>
            <TableCell style={{ textAlign: "right" }}>Agent Fee</TableCell>
            <TableCell style={{ textAlign: "right" }}>Agency Fee</TableCell>
            <TableCell style={{ textAlign: "right" }}>Commission</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow>

          {items.map((r, idx) => (
            <TableRow key={idx}>
              <TableCell>{r.policy_nbr}</TableCell>
              <TableCell>{fmtDateTime(r.created)}</TableCell>
              <TableCell>{fmtDate(r.effective_date)}</TableCell>
              <TableCell style={{ textTransform: "uppercase" }}>
                {r.client_name}
              </TableCell>
              <TableCell style={{ textAlign: "right" }}>
                ${fmtMoney(r.revised_net_premium)}
              </TableCell>
              <TableCell style={{ textAlign: "right" }}>
                ${fmtMoney(r.policy_fee)}
              </TableCell>
              <TableCell style={{ textAlign: "right" }}>
                ${fmtMoney(r.iva)}
              </TableCell>
              <TableCell style={{ textAlign: "right" }}>
                ${fmtMoney(r.total)}
              </TableCell>
              <TableCell style={{ textAlign: "right" }}>
                ${fmtMoney(r.agent_fee)}
              </TableCell>
              <TableCell style={{ textAlign: "right" }}>
                ${fmtMoney(r.agency_fee)}
              </TableCell>
              <TableCell style={{ textAlign: "right" }}>
                ${fmtMoney(r.commission)}
              </TableCell>
              <TableCell>{r.comments || r.reason || ""}</TableCell>
            </TableRow>
          ))}

          <TableRow style={{ background: COLORS.pastel2, fontWeight: 700 }}>
            <TableCell colSpan={3}>&nbsp;</TableCell>
            <TableCell>
              Total {tipo}s: {subtotal.quantity}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(subtotal.net)}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(subtotal.policy_fee)}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(subtotal.iva)}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(subtotal.total)}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(subtotal.agent_fee)}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(subtotal.agency_fee)}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              ${fmtMoney(subtotal.commission)}
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </React.Fragment>
      );
    });
  };

  return (
    <div className="container-fluid" style={{ padding: 20 }}>
      {/* Hidden form fields (kept for compatibility if your backend expects them) */}
      <form id="report_form">
        {Object.entries(forma || {}).map(([k, v]) => (
          <input key={k} readOnly type="hidden" name={k} value={v} />
        ))}
      </form>

      <div className="cl-mcont">
        <GridContainer style={{ paddingTop: 20 }}>
          <GridItem xs={12}>
            <div
              className="block-flat"
              style={{ padding: 20, background: "#fff", borderRadius: 8 }}
            >
              {/* Header */}
              <GridContainer style={{ marginBottom: 16 }}>
                <GridItem xs={12} sm={6}>
                  <Typography variant="h6">
                    {report_tz
                      ? `${timezones?.[report_tz]} - ${report_tz.toUpperCase()}`
                      : ""}
                  </Typography>
                  <Typography variant="h5">{title}</Typography>
                </GridItem>

                <GridItem xs={12} sm={6}>
                  <GridContainer>
                    <GridItem xs={12} sm={4}>
                      <Button
                        variant="green"
                        startIcon={<PrintIcon />}
                        onClick={() => onGeneratePdf(9, "pdf")}
                        style={{ width: "100%" }}
                      >
                        PDF
                      </Button>
                    </GridItem>

                    <GridItem xs={12} sm={4}>
                      <Button
                        variant="green"
                        startIcon={<TableRowsIcon />}
                        onClick={() => onGenerateCsv(9, "csv")}
                        style={{ width: "100%" }}
                      >
                        CSV
                      </Button>
                    </GridItem>

                    <GridItem xs={12} sm={4}>
                      <Button
                        style={{
                          background: "#FFF",
                          borderColor: COLORS.main,
                          color: COLORS.main,
                          border: "1px solid",
                          width: "100%",
                        }}
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                      >
                        Back To Reports
                      </Button>
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>

              {/* Date + Company */}
              <GridContainer style={{ marginBottom: 20 }}>
                <GridItem xs={12} sm={2}>
                  Date: {new Date().toLocaleString()}
                </GridItem>
                <GridItem
                  xs={12}
                  sm={10}
                  style={{ textAlign: "right", fontSize: 20 }}
                >
                  HDI Seguros, S.A. de C.V.
                </GridItem>
              </GridContainer>

              {/* Main table area: simplified structure per agency */}
              <TableContainer
                component={Paper}
              >
                <Table>
                  <TableBody>
                    {infos.map((info, idxA) => {
                      // For each agency compute agency totals simply
                      const agencyTotals = {
                        quantity: 0,
                        net: 0,
                        policy_fee: 0,
                        iva: 0,
                        total: 0,
                        agency_fee: 0,
                        agent_fee: 0,
                        commission: 0,
                      };

                      const addListToTotals = (list, sign = 1) =>
                        (list || []).forEach((r) => {
                          agencyTotals.quantity += sign * 1;
                          agencyTotals.net +=
                            sign * (Number(r.revised_net_premium) || 0);
                          agencyTotals.policy_fee +=
                            sign * (Number(r.policy_fee) || 0);
                          agencyTotals.iva += sign * (Number(r.iva) || 0);
                          agencyTotals.total += sign * (Number(r.total) || 0);
                          agencyTotals.agency_fee +=
                            sign * (Number(r.agency_fee) || 0);
                          agencyTotals.agent_fee +=
                            sign * (Number(r.agent_fee) || 0);
                          agencyTotals.commission +=
                            sign * (Number(r.commission) || 0);
                        });

                      addListToTotals(info.agent?.all || [], +1);
                      addListToTotals(info.guest?.all || [], +1);
                      addListToTotals(info.agent?.cancel || [], -1);
                      addListToTotals(info.guest?.cancel || [], -1);

                      return (
                        <React.Fragment key={idxA}>
                          {/* Agency Header */}
                          <TableRow>
                            <TableCell
                              colSpan={12}
                              style={{
                                background: COLORS.main,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              {info.agency?.code} — {info.agency?.name}
                            </TableCell>
                          </TableRow>

                          {/* Endorsements (Agent + Guest) */}
                          <TableRow>
                            <TableCell
                              colSpan={12}
                              style={{
                                background: COLORS.main,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              HDI Seguros Agency (B) Endorsements
                            </TableCell>
                          </TableRow>

                          {/* agent.all */}
                          <EndorsementTable
                            titleLabel="Agent Endorsements"
                            rows={info.agent?.all || []}
                          />

                          {/* guest.all */}
                          <EndorsementTable
                            titleLabel="Guest Endorsements"
                            rows={info.guest?.all || []}
                          />

                          {/* After endorsements show cancellations grouped by tipo */}
                          <TableRow>
                            <TableCell
                              colSpan={12}
                              style={{
                                background: COLORS.main,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              HDI Seguros Agency (B) Refunds &amp; Cancellations
                            </TableCell>
                          </TableRow>

                          <CancellationsByTipo
                            rows={info.agent?.cancel || []}
                          />
                          {/* show totals for cancellations (agent side) already included above */}

                          {/* Summarize agent totals for this agency (simple) */}
                          <TableRow style={{ background: COLORS.pastel2 }}>
                            <TableCell colSpan={3}>&nbsp;</TableCell>
                            <TableCell>
                              <strong>Agency Totals:</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(agencyTotals.net)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agencyTotals.policy_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(agencyTotals.iva)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(agencyTotals.total)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agencyTotals.agent_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agencyTotals.agency_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agencyTotals.commission)}
                              </strong>
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>

                          {/* Spacer */}
                          <TableRow>
                            <TableCell
                              colSpan={12}
                              style={{ background: COLORS.faded }}
                            >
                              &nbsp;
                            </TableCell>
                          </TableRow>

                          {/* Now HDI Seguros Agency (C) Endorsements */}
                          <TableRow>
                            <TableCell
                              colSpan={12}
                              style={{
                                background: COLORS.main,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              HDI Seguros Agency (C) Endorsements
                            </TableCell>
                          </TableRow>

                          <EndorsementTable
                            titleLabel="Guest Endorsements"
                            rows={info.guest?.all || []}
                          />

                          <TableRow>
                            <TableCell
                              colSpan={12}
                              style={{
                                background: COLORS.main,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              HDI Seguros Agency (C) Refunds &amp; Cancellations
                            </TableCell>
                          </TableRow>

                          <CancellationsByTipo
                            rows={info.guest?.cancel || []}
                          />

                          <TableRow style={{ background: COLORS.pastel2 }}>
                            <TableCell colSpan={3}>&nbsp;</TableCell>
                            <TableCell>
                              <strong>Customer Totals:</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(agencyTotals.net)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agencyTotals.policy_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(agencyTotals.iva)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(agencyTotals.total)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agencyTotals.agent_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agencyTotals.agency_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agencyTotals.commission)}
                              </strong>
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>

                          {/* Spacer */}
                          <TableRow>
                            <TableCell
                              colSpan={12}
                              style={{ background: COLORS.faded }}
                            >
                              &nbsp;
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Bottom Buttons */}
              <GridContainer style={{ marginTop: 16 }}>
                <GridItem xs={12} sm={6} />
                <GridItem xs={12} sm={6}>
                  <GridContainer>
                    <GridItem xs={12} sm={4}>
                      <Button
                        variant="green"
                        startIcon={<PrintIcon />}
                        onClick={() => onGeneratePdf(9, "pdf")}
                        style={{ width: "100%" }}
                      >
                        PDF
                      </Button>
                    </GridItem>

                    <GridItem xs={12} sm={4}>
                      <Button
                        variant="green"
                        startIcon={<TableRowsIcon />}
                        onClick={() => onGenerateCsv(9, "csv")}
                        style={{ width: "100%" }}
                      >
                        CSV
                      </Button>
                    </GridItem>

                    <GridItem xs={12} sm={4}>
                      <Button
                        style={{
                          background: "#FFF",
                          borderColor: COLORS.main,
                          color: COLORS.main,
                          border: "1px solid",
                          width: "100%",
                        }}
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                      >
                        Back To Reports
                      </Button>
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            </div>
          </GridItem>
        </GridContainer>
      </div>
    </div>
  );
}
